/**
 * D2c: evidence requests and the AI sufficiency check.
 * Backend: app/django-core/apps/evidence_requests/ (urls.py, views.py), mounted at
 * /api/v1/evidence-requests/.
 *
 * - The tenant comes from the session; the client never sends one.
 * - Submitting sends multipart (`files` repeated, `note`, `linked` as a JSON string).
 * - Staged files are streamed by a tenant-scoped action, fetched as a blob with the auth header.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
import { complianceApi } from '../../compliance/api/complianceApi';
import { controlApi } from '../../control/api/controlApi';
import { auditApi } from '../../audit/api/auditApi';

const BASE = '/api/v1/evidence-requests';

/** One path segment from an id, so "../" in an id can never reach another endpoint. */
const seg = (id) => encodeURIComponent(String(id));

const compact = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined)
  );

const filenameFromDisposition = (header) => {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || null;
};

const saveBlob = (blob, filename) => {
  if (typeof window === 'undefined' || !window.URL?.createObjectURL) return;
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
};

/** A blob download through a tenant-scoped endpoint (never a storage URL). */
const downloadBlob = async (baseQuery, url, filename) => {
  const result = await baseQuery({
    url,
    responseHandler: async (response) => {
      if (response.ok) return response.blob();
      // a proxy error (502 HTML) is not JSON: never throw while reading the error body
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch {
        return { error: 'The file could not be downloaded.' };
      }
    },
  });
  if (result.error) return { error: result.error };
  const name =
    filenameFromDisposition(result.meta?.response?.headers?.get('content-disposition')) ||
    filename ||
    'evidence';
  saveBlob(result.data, name);
  return { data: { filename: name } };
};

/** Download route per evidence store a submission can link to (submissions' `linked_items`). */
const LINKED_DOWNLOAD = {
  'compliance.Evidence': (id) => `/api/v1/compliance/evidence/${seg(id)}/download/`,
  'control.ControlEvidence': (id) => `/api/v1/controls/evidence/${seg(id)}/download/`,
  'audit.AuditEvidence': (id) => `/api/v1/audits/evidence/${seg(id)}/download/`,
};

/**
 * Accept files evidence into another slice's store (and, for audit, a workpaper). RTK Query
 * tags do not cross slices, so those slices' cached lists and link pickers are invalidated
 * with their own `util.invalidateTags`.
 */
const refreshEvidenceStores = async (_arg, { dispatch, queryFulfilled }) => {
  try {
    await queryFulfilled;
  } catch {
    return;
  }
  dispatch(complianceApi.util.invalidateTags(['Evidence']));
  dispatch(controlApi.util.invalidateTags(['ControlEvidence']));
  dispatch(auditApi.util.invalidateTags(['Evidence', 'Workpaper']));
};

const action = (name) => ({
  query: ({ id, ...body }) => ({
    url: `${BASE}/${seg(id)}/${name}/`,
    method: 'POST',
    body: Object.keys(body).length ? body : undefined,
  }),
  invalidatesTags: (r, e, { id }) => [
    { type: 'EvidenceRequest', id },
    { type: 'EvidenceRequest', id: 'LIST' },
    { type: 'Submission', id },
  ],
});

export const evidenceRequestsApi = createApi({
  reducerPath: 'evidenceRequestsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['EvidenceRequest', 'Submission'],
  endpoints: (builder) => ({
    getEvidenceRequests: builder.query({
      query: (params = {}) => ({ url: `${BASE}/`, params: compact(params) }),
      providesTags: (result) => [
        { type: 'EvidenceRequest', id: 'LIST' },
        ...(result?.results || []).map(({ id }) => ({ type: 'EvidenceRequest', id })),
      ],
    }),
    getEvidenceRequest: builder.query({
      query: (id) => ({ url: `${BASE}/${seg(id)}/` }),
      providesTags: (r, e, id) => [{ type: 'EvidenceRequest', id }],
    }),
    createEvidenceRequest: builder.mutation({
      query: (body) => ({ url: `${BASE}/`, method: 'POST', body }),
      invalidatesTags: [{ type: 'EvidenceRequest', id: 'LIST' }],
    }),
    // Read-only: the requirement text the item suggests, and its label.
    getEvidenceRequestPrefill: builder.query({
      query: ({ target_type, target_id }) => ({
        url: `${BASE}/prefill/`,
        params: { target_type, target_id },
      }),
    }),
    // PBC list: one request per row for an engagement, `procedure` optional per row.
    createEvidenceBatch: builder.mutation({
      query: ({ engagement, items }) => ({
        url: `${BASE}/batch/`,
        method: 'POST',
        body: { engagement, items },
      }),
      invalidatesTags: [{ type: 'EvidenceRequest', id: 'LIST' }],
    }),
    updateEvidenceRequest: builder.mutation({
      query: ({ id, ...body }) => ({ url: `${BASE}/${seg(id)}/`, method: 'PATCH', body }),
      invalidatesTags: (r, e, { id }) => [
        { type: 'EvidenceRequest', id },
        { type: 'EvidenceRequest', id: 'LIST' },
      ],
    }),
    reassignEvidenceRequest: builder.mutation(action('reassign')),
    cancelEvidenceRequest: builder.mutation(action('cancel')),
    startEvidenceReview: builder.mutation(action('start-review')),
    returnEvidenceRequest: builder.mutation(action('return')),
    acceptEvidenceRequest: builder.mutation({
      ...action('accept'),
      onQueryStarted: refreshEvidenceStores,
    }),
    submitEvidence: builder.mutation({
      query: ({ id, formData }) => ({
        url: `${BASE}/${seg(id)}/submit/`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (r, e, { id }) => [
        { type: 'EvidenceRequest', id },
        { type: 'EvidenceRequest', id: 'LIST' },
        { type: 'Submission', id },
      ],
    }),
    getEvidenceSubmissions: builder.query({
      query: (id) => ({ url: `${BASE}/${seg(id)}/submissions/` }),
      providesTags: (r, e, id) => [{ type: 'Submission', id }],
    }),
    getEvidenceWorkpapers: builder.query({
      query: (id) => ({ url: `${BASE}/${seg(id)}/workpapers/` }),
      providesTags: (r, e, id) => [{ type: 'EvidenceRequest', id }],
    }),
    checkEvidenceSufficiency: builder.mutation({
      query: ({ id }) => ({ url: `${BASE}/${seg(id)}/check/`, method: 'POST' }),
      invalidatesTags: (r, e, { id }) => [{ type: 'Submission', id }],
    }),
    downloadSubmissionFile: builder.mutation({
      queryFn: ({ fileId, filename }, _api, _extra, baseQuery) =>
        downloadBlob(baseQuery, `${BASE}/submission-files/${seg(fileId)}/download/`, filename),
    }),
    // Evidence a submission links to, from its own store's download action; that action
    // applies the store's own permission and tenant checks.
    downloadLinkedEvidence: builder.mutation({
      queryFn: ({ store, id, filename }, _api, _extra, baseQuery) => {
        const route = LINKED_DOWNLOAD[store];
        if (!route) {
          return {
            error: { status: 'CUSTOM_ERROR', data: { error: 'This evidence has no file.' } },
          };
        }
        return downloadBlob(baseQuery, route(id), filename);
      },
    }),
  }),
});

export const {
  useGetEvidenceRequestsQuery,
  useGetEvidenceRequestQuery,
  useCreateEvidenceRequestMutation,
  useGetEvidenceRequestPrefillQuery,
  useLazyGetEvidenceRequestPrefillQuery,
  useCreateEvidenceBatchMutation,
  useUpdateEvidenceRequestMutation,
  useReassignEvidenceRequestMutation,
  useCancelEvidenceRequestMutation,
  useStartEvidenceReviewMutation,
  useReturnEvidenceRequestMutation,
  useAcceptEvidenceRequestMutation,
  useSubmitEvidenceMutation,
  useGetEvidenceSubmissionsQuery,
  useGetEvidenceWorkpapersQuery,
  useCheckEvidenceSufficiencyMutation,
  useDownloadSubmissionFileMutation,
  useDownloadLinkedEvidenceMutation,
} = evidenceRequestsApi;
